export interface PaginationMeta {
  cursor: string | null;
  hasMore: boolean;
  total: number;
}

export interface PaginatedResponse<T> {
  data: T[];
  pagination: PaginationMeta;
}

export interface PaginationParams {
  limit?: number;
  cursor?: string;
  page?: number;
}

// Offset/page-based pagination (e.g. GET /purchasers/parts), where the server
// returns `meta` rather than the cursor-based `pagination` above.
export interface PageMeta {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface PagePaginatedResponse<T> {
  data: T[];
  meta: PageMeta;
}

// Build the query params for a paginated request, omitting anything unset so we
// don't send empty `cursor`/`limit`/`page` values. Numeric fields are
// stringified for use as URL query params.
export function buildPaginationParams(pagination?: PaginationParams): Record<string, string> {
  const params: Record<string, string> = {};
  if (pagination?.cursor) params.cursor = pagination.cursor;
  if (pagination?.limit !== undefined) params.limit = String(pagination.limit);
  if (pagination?.page !== undefined) params.page = String(pagination.page);
  return params;
}

// Endpoints often echo `page`/`limit` straight from the (un-coerced) query
// string, so `meta.page` can arrive as e.g. "1". Normalise every field to a
// number so page arithmetic (`page + 1`) doesn't silently string-concatenate.
export function normalizePageMeta<T>(response: PagePaginatedResponse<T>): PagePaginatedResponse<T> {
  const { data, meta } = response;
  return {
    data,
    meta: {
      page: Number(meta?.page ?? 1),
      limit: Number(meta?.limit ?? data.length),
      total: Number(meta?.total ?? data.length),
      totalPages: Number(meta?.totalPages ?? 1),
    },
  };
}
